'use client';

import type { AdpopcornPlacementKey } from '@/constants/adpopcorn';
import {
  ADPOPCORN_INTERNAL_TEST_MEMBER_OAUTH_IDS,
  ADPOPCORN_PLACEMENT_IDS,
} from '@/constants/adpopcorn';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';

import { useAdGuards } from './useAdGuards';

export type AdpopcornStatus = 'unsupported' | 'app-install' | 'login' | 'ready';

interface UseAdpopcornOptions {
  placement: AdpopcornPlacementKey;
}

interface UseAdpopcornPlacementReturn {
  status: AdpopcornStatus;
  placementId: string | null;
}

interface UseAdpopcornReturn {
  status: AdpopcornStatus;
  placementId: string | null;
  onClick: () => void;
  isOpening: boolean;
}

export const useAdpopcornPlacement = ({
  placement,
}: UseAdpopcornOptions): UseAdpopcornPlacementReturn => {
  const { isMounted, isWelloApp, isExternalWebview, osType } = useAdGuards();
  const memberOAuth = useAuthStore((state) => state.myInfo?.memberOAuth);
  const placementConfig = ADPOPCORN_PLACEMENT_IDS[placement];

  const placementId =
    osType === 'ios' || osType === 'android' ? placementConfig[osType] : null;

  const status: AdpopcornStatus = (() => {
    if (!isMounted || isExternalWebview) return 'unsupported';
    //* [임시] 내부 테스트: 화이트리스트에 포함된 멤버에게만 노출한다.
    if (
      !memberOAuth ||
      !ADPOPCORN_INTERNAL_TEST_MEMBER_OAUTH_IDS.includes(memberOAuth)
    )
      return 'unsupported';
    if (!isWelloApp) return 'app-install';
    if (!memberOAuth) return 'login';
    if (!placementId) return 'unsupported';

    return 'ready';
  })();

  return { status, placementId };
};

export const useAdpopcorn = ({
  placement,
}: UseAdpopcornOptions): UseAdpopcornReturn => {
  const { status, placementId } = useAdpopcornPlacement({ placement });
  const setIsShowAppInstallConfirmModal = useGlobalStore(
    (state) => state.setIsShowAppInstallConfirmModal,
  );
  const setIsShowLoginRedirectionConfirmModal = useGlobalStore(
    (state) => state.setIsShowLoginRedirectionConfirmModal,
  );
  const { request, isPending } = useHandleApp({
    type: MESSAGE_KEYWORD.OPEN_ADPOPCORN_BRIDGE,
  });

  const onClick = () => {
    if (status === 'app-install') {
      setIsShowAppInstallConfirmModal(true);

      return;
    }

    if (status === 'login') {
      setIsShowLoginRedirectionConfirmModal(true);

      return;
    }

    if (status === 'ready') {
      request({ placementId });
    }
  };

  return {
    status,
    placementId,
    onClick,
    isOpening: isPending,
  };
};
